/**
 * RelatedResourceRail — compact contextual links for manuscript/app panels.
 *
 * Intended for sidebars and right rails where a panel needs to point at
 * related chapters, app panels, runbooks, or figures without each demo
 * inventing another card/list shape.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { ArrowRight, PanelRight } from "lucide-react";
import React from "react";
import { GRAY, PRIMARY } from "../../design-system/tokens.js";

interface RelatedResourceItemBase {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly badge?: string;
  readonly icon?: React.ReactNode;
}

export interface RelatedResourceLinkItem extends RelatedResourceItemBase {
  readonly href: string;
  readonly onSelect?: never;
}

export interface RelatedResourceActionItem extends RelatedResourceItemBase {
  readonly onSelect: () => void;
  readonly href?: never;
}

export type RelatedResourceItem = RelatedResourceLinkItem | RelatedResourceActionItem;

export interface RelatedResourceSection {
  readonly id: string;
  readonly title: string;
  readonly items: readonly RelatedResourceItem[];
}

export interface RelatedResourceRailProps {
  readonly title?: string;
  readonly description?: string;
  readonly sections: readonly RelatedResourceSection[];
  readonly footer?: React.ReactNode;
}

const RAIL_STYLE: React.CSSProperties = {
  display: "grid",
  gap: 14,
};

const ITEM_STYLE: React.CSSProperties = {
  alignItems: "center",
  background: "white",
  border: `1px solid ${GRAY[300]}`,
  borderRadius: 8,
  color: GRAY[900],
  display: "grid",
  gap: 3,
  padding: "9px 10px",
  textAlign: "left",
  textDecoration: "none",
  width: "100%",
};

function ResourceItem({ item }: { readonly item: RelatedResourceItem }): React.JSX.Element {
  const content = (
    <>
      <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
        {item.icon ?? <ArrowRight aria-hidden="true" size={13} color={PRIMARY[700]} />}
        <span style={{ fontSize: 12, fontWeight: 800 }}>{item.label}</span>
        {item.badge !== undefined ? (
          <span
            style={{
              color: PRIMARY[700],
              fontSize: 10,
              fontWeight: 800,
              marginLeft: "auto",
            }}
          >
            {item.badge}
          </span>
        ) : null}
      </span>
      {item.description !== undefined ? (
        <span style={{ color: GRAY[600], fontSize: 11, lineHeight: 1.4 }}>{item.description}</span>
      ) : null}
    </>
  );

  if (item.href !== undefined) {
    return (
      <a href={item.href} style={ITEM_STYLE}>
        {content}
      </a>
    );
  }

  return (
    <button type="button" onClick={item.onSelect} style={ITEM_STYLE}>
      {content}
    </button>
  );
}

export function RelatedResourceRail({
  title = "Related",
  description,
  sections,
  footer,
}: RelatedResourceRailProps): React.JSX.Element {
  return (
    <aside style={RAIL_STYLE}>
      <header style={{ display: "grid", gap: 3 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <PanelRight aria-hidden="true" size={15} color={PRIMARY[700]} />
          <h2 style={{ color: GRAY[900], fontSize: 12, fontWeight: 900, margin: 0 }}>{title}</h2>
        </div>
        {description !== undefined ? (
          <p style={{ color: GRAY[600], fontSize: 11, lineHeight: 1.45, margin: 0 }}>
            {description}
          </p>
        ) : null}
      </header>
      {sections.map((section) => (
        <section key={section.id} style={{ display: "grid", gap: 7 }}>
          <div
            style={{
              color: GRAY[700],
              fontSize: 10,
              fontWeight: 900,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            {section.title}
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {section.items.map((item) => (
              <ResourceItem key={item.id} item={item} />
            ))}
          </div>
        </section>
      ))}
      {footer !== undefined ? <div>{footer}</div> : null}
    </aside>
  );
}
